/**
 * Relatórios da carteira e observação enriquecida do extrato (M7, task 17tm466nz8b).
 *
 * Espelha o contrato publicado pelo m7-back em `~/trabalhos/m7-coord.md` (14/10/2026),
 * endpoints `/api/wallet/navigation`, `/api/wallet/entries?include=observation`,
 * `/api/wallet/entries/{id}/items` e `/api/wallet/reports/*`. Nomes de campo e valores de
 * enum são os do contrato — qualquer ajuste de forma vem de lá, não de suposição do front.
 *
 * Nenhum tipo aqui tem campo de status de ENTREGA na observação do extrato (ajuste do
 * Misael, 14/10): status de entrega só existe na coluna `delivery_status` dos relatórios
 * (tipo `dispatches`) e nas telas de massivo, nunca em `WalletEntryObservation`.
 */

// ---------------------------------------------------------------------------
// Navegação do menu (GET /api/wallet/navigation)
// ---------------------------------------------------------------------------

export interface WalletNavigation {
  reports_enabled: boolean;
  wallet_migrated: { sms: boolean; call: boolean };
  hide: { messages_report: boolean; lead_responses: boolean; recharges: boolean };
}

// ---------------------------------------------------------------------------
// Observação do lançamento (GET /api/wallet/entries?include=observation)
// ---------------------------------------------------------------------------

export type WalletObservationKind =
  | 'sms_chunk'
  | 'sms_broadcast'
  | 'sms_resend'
  | 'wa_control'
  | 'wa_broadcast'
  | 'wa_reply'
  | 'ai_usage'
  | 'call_lead'
  | 'recharge'
  | 'recharge_bonus'
  | 'recharge_reversal'
  | 'transfer'
  | 'migration'
  | 'unknown';

export type WalletObservationChannel = 'sms' | 'whatsapp' | 'call' | 'ai' | null;

export type WalletObservationOriginType =
  | 'automation'
  | 'broadcast'
  | 'conversation'
  | 'manual_resend'
  | 'partner_api'
  | 'recharge'
  | 'system';

export interface WalletObservationOriginItem {
  name: string;
  step: number | null;
  count: number;
}

export interface WalletObservationOrigin {
  type: WalletObservationOriginType;
  label: string;
  items: WalletObservationOriginItem[];
}

export type WalletObservationContent =
  | { type: 'text'; text: string }
  | { type: 'template'; name: string }
  | { type: 'audio'; name: string }
  | null;

export interface WalletObservationConversation {
  id: string;
  path: string;
}

export interface WalletObservationItems {
  count: number;
  /** `true` quando o painel do lote (`/entries/{id}/items`) pode ser aberto. */
  browsable: boolean;
}

export interface WalletObservationCall {
  duration_seconds: number;
  blocks: number;
}

export interface WalletObservationAi {
  agent_name: string | null;
  input_tokens: number;
  output_tokens: number;
}

export type WalletInvoiceStatus = 'pending' | 'processing' | 'issued' | 'failed' | 'not_applicable';

export interface WalletObservationInvoice {
  status: WalletInvoiceStatus;
  /** Só vem preenchida quando `status === 'issued'`. */
  url: string | null;
}

export interface WalletObservationRecharge {
  id: string;
  payment_type: 'pix' | 'card' | 'boleto' | 'transfer' | 'migration' | null;
  invoice: WalletObservationInvoice | null;
}

/**
 * Observação de um lançamento do extrato. Nenhum campo de status de ENTREGA aqui —
 * é a regra do ajuste 2 do Misael (14/10), vale para todos os canais.
 */
export interface WalletEntryObservation {
  kind: WalletObservationKind;
  channel: WalletObservationChannel;
  /** Texto pronto do back, uma linha, já com os dados relevantes do lançamento. */
  summary: string;
  /** Mascarado pelo back: BR `(11) 9****-4321`; não-BR `+1 ***-0101`. */
  destination: string | null;
  lead_name: string | null;
  content: WalletObservationContent;
  origin: WalletObservationOrigin | null;
  conversation: WalletObservationConversation | null;
  items: WalletObservationItems;
  call: WalletObservationCall | null;
  ai: WalletObservationAi | null;
  recharge: WalletObservationRecharge | null;
  /** `false` = lote mais antigo que a retenção: mostrar "detalhe arquivado", sem esconder o valor. */
  detail_available: boolean;
  /** Só quando o lançamento é um lote (`items.count > 1`); senão `null`. */
  unit_cost_micros: number | null;
}

// ---------------------------------------------------------------------------
// Painel do lote (GET /api/wallet/entries/{entry_id}/items)
// ---------------------------------------------------------------------------

export interface WalletBatchItem {
  position: number;
  sent_at: string;
  destination: string;
  lead_name: string | null;
  text: string;
  origin_name: string | null;
  step: number | null;
}

export interface WalletBatchItemsPage {
  entry_id: string;
  kind: WalletObservationKind;
  channel: WalletObservationChannel;
  total: number;
  found: number;
  /** `false` = parte do lote está arquivada (fora da retenção): avisar, sem esconder o resto. */
  detail_complete: boolean;
  unit_cost_micros: number | null;
  page: number;
  per_page: number;
  last_page: number;
  data: WalletBatchItem[];
}

export interface WalletBatchItemsFilters {
  at: string;
  page?: number;
  per_page?: number;
  q?: string;
}

// ---------------------------------------------------------------------------
// Catálogo de relatórios (GET /api/wallet/reports/catalog)
// ---------------------------------------------------------------------------

export type WalletReportType = 'dispatches' | 'entries' | 'recharges' | 'consumption';
// Não existe 'responses'/"Respostas recebidas": cancelado pelo Misael (14/10).

export type WalletReportFormat = 'csv_br' | 'xlsx';
// Só os 2 — sem "CSV internacional" (ajuste do Misael).

export type WalletReportColumnType = 'datetime' | 'date' | 'text' | 'phone' | 'integer' | 'money' | 'bool';

export interface WalletReportColumnDef {
  key: string;
  label: string;
  type: WalletReportColumnType;
  default: boolean;
}

export interface WalletReportTypeFilters {
  channels?: string[];
  origins?: string[];
  statuses?: string[];
  entry_types?: string[];
  services?: string[];
  payment_statuses?: string[];
  kinds?: string[];
  invoice_statuses?: string[];
  group_by?: string[];
  split_by_origin?: 'bool';
}

export interface WalletReportTypeDef {
  key: WalletReportType;
  label: string;
  filters: WalletReportTypeFilters;
  columns: WalletReportColumnDef[];
}

export interface WalletReportFormatDef {
  key: WalletReportFormat;
  label: string;
}

export interface WalletReportsCatalog {
  limits: {
    immediate_rows: number;
    max_rows: number;
    max_period_days: number;
    ttl_days: number;
    preview_rows: number;
  };
  formats: WalletReportFormatDef[];
  types: WalletReportTypeDef[];
}

// ---------------------------------------------------------------------------
// Configuração do relatório (estado do configurador / modelos salvos)
// ---------------------------------------------------------------------------

/**
 * Atalho de período da UI — conceito só do FRONT (botões Hoje/7/30/90 dias/Mês
 * passado/Personalizado). O contrato só conhece `filters.from`/`to` (datas
 * `AAAA-MM-DD`, ver `~/trabalhos/m7-coord.md`); é o configurador que resolve o
 * atalho em datas antes de pedir a prévia/exportação.
 */
export type WalletPeriodPreset = 'today' | '7d' | '30d' | '90d' | 'last_month' | 'custom';

export interface WalletReportFilters {
  from: string | null;
  to: string | null;
  channels: string[];
  origins: string[];
  statuses: string[];
  entry_types: string[];
  services: string[];
  payment_statuses: string[];
  kinds: string[];
  invoice_statuses: string[];
  group_by: 'day' | 'week' | 'month' | null;
  split_by_origin: boolean;
  entry_ids: string[];
}

export interface WalletReportColumnConfig {
  key: string;
  label: string;
  visible: boolean;
}

export interface WalletReportConfig {
  type: WalletReportType;
  filters: WalletReportFilters;
  columns: WalletReportColumnConfig[];
  format: WalletReportFormat;
}

// ---------------------------------------------------------------------------
// Prévia (POST /api/wallet/reports/preview)
// ---------------------------------------------------------------------------

export interface WalletReportPreviewColumn {
  letter: string;
  key: string;
  label: string;
  type: WalletReportColumnType;
  align: 'left' | 'right';
}

export type WalletReportPreviewEstimateMode = 'immediate' | 'background';

export interface WalletReportPreviewEstimate {
  rows: number;
  exact: boolean;
  mode: WalletReportPreviewEstimateMode;
}

export interface WalletReportPreview {
  columns: WalletReportPreviewColumn[];
  /** Já formatada em pt-BR, exatamente como vai para o arquivo — o front não reformata. */
  rows: string[][];
  csv_sample: string;
  estimate: WalletReportPreviewEstimate;
  /** `true` só no tipo `consumption`: quando o período não cabe todo na prévia. */
  partial: boolean;
}

// ---------------------------------------------------------------------------
// Exportações (POST/GET/DELETE /api/wallet/reports/exports)
// ---------------------------------------------------------------------------

export type WalletReportExportStatus = 'queued' | 'running' | 'ready' | 'failed' | 'expired';
export type WalletReportExportMode = 'immediate' | 'background';

export interface WalletReportExport {
  id: string;
  type: WalletReportType;
  type_label?: string;
  format: WalletReportFormat;
  status: WalletReportExportStatus;
  mode?: WalletReportExportMode;
  estimated_rows: number | null;
  row_count: number | null;
  file_name: string | null;
  file_size: number | null;
  error: string | null;
  params_summary?: string;
  params?: { type: WalletReportType; filters: WalletReportFilters; columns: WalletReportColumnConfig[]; format: WalletReportFormat };
  created_at: string;
  finished_at: string | null;
  expires_at: string | null;
}

export interface WalletReportExportsPage {
  data: WalletReportExport[];
  meta: { page: number; per_page: number; total: number };
}

export interface WalletReportDownloadUrl {
  url: string;
  expires_at: string;
}

// ---------------------------------------------------------------------------
// Modelos salvos (GET/POST/PUT/DELETE /api/wallet/reports/templates)
// ---------------------------------------------------------------------------

export type WalletReportTemplatePeriod = 'last_30_days' | 'this_month' | 'last_month' | 'custom';

export interface WalletReportTemplateConfig {
  filters: WalletReportFilters & { period?: WalletReportTemplatePeriod };
  columns: WalletReportColumnConfig[];
  format: WalletReportFormat;
}

export interface WalletReportTemplate {
  id: string;
  name: string;
  type: WalletReportType;
  config: WalletReportTemplateConfig;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Helpers puros
// ---------------------------------------------------------------------------

/** Filtros de configuração vazios — ponto de partida do configurador para um tipo novo. */
export function emptyReportFilters(): WalletReportFilters {
  return {
    from: null,
    to: null,
    channels: [],
    origins: [],
    statuses: [],
    entry_types: [],
    services: [],
    payment_statuses: [],
    kinds: [],
    invoice_statuses: [],
    group_by: null,
    split_by_origin: false,
    entry_ids: [],
  };
}

/** Config inicial a partir das colunas padrão (`default: true`) do tipo no catálogo. */
export function defaultColumnsConfig(def: WalletReportTypeDef): WalletReportColumnConfig[] {
  return def.columns.map((column) => ({ key: column.key, label: column.label, visible: column.default }));
}
